import { isHexString, ParamType } from "ethers";
import { Operator, ParameterType } from "zodiac-roles-deployments";
import { coercePermission } from "../../../permission/coercePermission";
import { checkParameterTypeCompatibility } from "../../../condition/conditionIntegrity";
import { describeStructure } from "./helpers/describeStructure";
import { parameterType } from "./helpers/parameterType";
import { and } from "./branching";
import { bitmask, eq } from "./comparison";
/**
 * Matches a tuple or array against a structure of conditions.
 *
 * Throws if the type is not a tuple or array. Throws if the structure of conditions is incompatible with the type.
 * @param scoping The conditions on the tuple or array elements
 */
export const matches = (scoping) => (abiType) => {
    // The type system allows for nesting matches(matches()).
    // While using this in practice would not make too much sense, we must make sure it's valid nonetheless.
    if (typeof scoping === "function") {
        return scoping(abiType);
    }
    let conditions;
    if (abiType.baseType === "tuple" && !abiType.components) {
        throw new Error("Tuple type must have components");
    }
    if (abiType.baseType === "array" && !abiType.arrayChildren) {
        throw new Error("Array type must have arrayChildren");
    }
    if (Array.isArray(scoping)) {
        // scoping is an array (TupleScopings)
        // supported for tuple and array types
        if (abiType.baseType !== "tuple" && abiType.baseType !== "array") {
            throw new Error(`Can only use \`matches\` on tuple or array type params, got: ${abiType.type}`);
        }
        // map scoping items to conditions
        conditions = scoping.map((scoping, index) => mapScoping(scoping, abiType.baseType === "tuple"
            ? abiType.components[index]
            : abiType.arrayChildren));
    }
    else {
        // scoping is an object (StructScoping)
        // only supported for tuple types
        if (abiType.baseType !== "tuple") {
            throw new Error(`Can only use \`matches\` with scoping object on tuple type params, got: ${abiType.type}`);
        }
        // map scoping values to conditions
        conditions = abiType.components.map((componentType) => mapScoping(scoping[componentType.name], componentType));
    }
    // sanity checks
    assertValidConditionsLength(conditions, abiType);
    assertValidConditionsKeys(scoping, abiType);
    assertCompatibleParamTypes(conditions, abiType);
    return {
        paramType: parameterType(abiType),
        operator: Operator.Matches,
        children: conditions.map((condition, index) => condition ||
            describeStructure(abiType.baseType === "tuple"
                ? abiType.components[index]
                : abiType.arrayChildren)),
    };
};
const calldataMatchesScopings = (scopings, abiTypes, options = {}) => (abiType) => {
    const paramTypes = abiTypes.map((abiType) => ParamType.from(abiType));
    // only supported at the top level or for bytes type params
    if (abiType && abiType.type !== "bytes") {
        throw new Error(`Can only use \`calldataMatches\` on bytes type params, got: ${abiType.type}`);
    }
    const { selector, etherWithinAllowance, callWithinAllowance } = options;
    // map scoping items to conditions
    const conditions = paramTypes.map((type, index) => mapScoping(scopings[index], type));
    // sanity checks
    assertValidConditionsKeys(conditions, paramTypes);
    assertCompatibleParamTypes(conditions, paramTypes);
    const matchesCondition = {
        paramType: ParameterType.Calldata,
        operator: Operator.Matches,
        children: conditions.map((condition, index) => condition || describeStructure(paramTypes[index])),
    };
    if (etherWithinAllowance) {
        matchesCondition.children.push({
            paramType: ParameterType.None,
            operator: Operator.EtherWithinAllowance,
            compValue: etherWithinAllowance,
        });
    }
    if (callWithinAllowance) {
        matchesCondition.children.push({
            paramType: ParameterType.None,
            operator: Operator.CallWithinAllowance,
            compValue: callWithinAllowance,
        });
    }
    if (selector) {
        if (selector.length !== 10) {
            throw new Error(`Selector must be exactly 4 bytes long, got: ${selector.length}`);
        }
        return and(() => matchesCondition, bitmask({ mask: "0xffffffff", value: selector }))(ParamType.from("bytes"));
    }
    return matchesCondition;
};
const calldataMatchesFunctionPermission = (functionPermission) => (abiType) => {
    // only supported at the top level or for bytes type params
    if (abiType && abiType.type !== "bytes") {
        throw new Error(`Can only use \`calldataMatches\` on bytes type params, got: ${abiType.type}`);
    }
    const { selector, condition } = coercePermission(functionPermission);
    if (condition) {
        if (condition.operator !== Operator.Matches ||
            condition.paramType !== ParameterType.Calldata) {
            throw new Error(`calldataMatches expects a function permission with an \`Operator.matches\`, \`ParamType.Calldata\` condition, got: \`Operator.${Operator[condition.operator]}\`, \`ParamType.${ParameterType[condition.paramType]}\``);
        }
    }
    const selectorCondition = bitmask({
        mask: "0xffffffff0000000000000000000000",
        value: selector,
    });
    return (condition ? and(() => condition, selectorCondition) : selectorCondition)(ParamType.from("bytes"));
};
export const calldataMatches = (scopingsOrFunctionPermission, abiTypes, options) => {
    return abiTypes
        ? calldataMatchesScopings(scopingsOrFunctionPermission, abiTypes, options)
        : calldataMatchesFunctionPermission(scopingsOrFunctionPermission);
};
/**
 * Matches standard ABI encoded bytes against a structure of conditions.
 *
 * @param scoping The conditions structure over the decoded parameters
 * @param abiTypes The parameter types defining how to decode bytes
 **/
export const abiEncodedMatches = (scopings, abiTypes) => (abiType) => {
    const paramTypes = abiTypes.map((abiType) => ParamType.from(abiType));
    // only supported at the top level or for bytes type params
    if (abiType && abiType.type !== "bytes") {
        throw new Error(`Can only use \`abiEncodedMatches\` on bytes type params, got: ${abiType.type}`);
    }
    // map scoping items to conditions
    const conditions = paramTypes.map((type, index) => mapScoping(scopings[index], type));
    // sanity checks
    assertValidConditionsKeys(conditions, paramTypes);
    assertCompatibleParamTypes(conditions, paramTypes);
    return {
        paramType: ParameterType.AbiEncoded,
        operator: Operator.Matches,
        children: conditions.map((condition, index) => condition || describeStructure(paramTypes[index])),
    };
};
/**
 * Maps a scoping (shortcut notation or condition function) to a condition.
 * @param scoping The scoping to map.
 * @param abiType The abi type of the parameter the scoping applies to.
 * @returns
 */
export function mapScoping(scoping, abiType) {
    if (scoping === undefined) {
        return undefined;
    }
    let conditionFunction;
    if (typeof scoping === "function") {
        // scoping is already a condition function
        conditionFunction = scoping;
    }
    else if (typeof scoping === "boolean" ||
        typeof scoping === "string" ||
        typeof scoping === "number" ||
        Array.isArray(scoping) ||
        isBigNumberish(scoping)) {
        // placeholders, primitive values, and arrays default to eq condition
        conditionFunction = eq(scoping);
    }
    else {
        // object values default to matches condition
        conditionFunction = matches(scoping);
    }
    return conditionFunction(abiType);
}
const assertValidConditionsLength = (conditions, typeOrTypes) => {
    let expectedLength;
    if (Array.isArray(typeOrTypes)) {
        expectedLength = typeOrTypes.length;
    }
    else if (typeOrTypes.baseType === "tuple" && typeOrTypes.components) {
        expectedLength = typeOrTypes.components.length;
    }
    else if (typeOrTypes.baseType === "array") {
        expectedLength = typeOrTypes.arrayLength || -1;
    }
    else {
        throw new Error(`Can only use \`matches\` on tuple or array type params, got: \`${typeOrTypes.format("sighash")}\``);
    }
    if (expectedLength <= 0)
        return;
    if (conditions.length > expectedLength) {
        throw new Error(`The conditions structure has too many elements (expected length: ${expectedLength}, conditions length: ${conditions.length})`);
    }
};
const assertValidConditionsKeys = (structure, typeOrTypes) => {
    if (Array.isArray(structure))
        return;
    if (!Array.isArray(typeOrTypes) && typeOrTypes.baseType !== "tuple")
        return;
    const types = Array.isArray(typeOrTypes)
        ? typeOrTypes
        : typeOrTypes.components;
    const unusedStructureKeys = arrayDiff(Object.keys(structure), types.map((type) => type.name));
    if (unusedStructureKeys.length > 0) {
        throw new Error(`The conditions structure has unknown keys: ${unusedStructureKeys.join(", ")}`);
    }
};
const assertCompatibleParamTypes = (conditions, typeOrTypes) => {
    conditions.forEach((condition, index) => {
        if (!condition)
            return;
        let type;
        if (Array.isArray(typeOrTypes)) {
            type = typeOrTypes[index];
        }
        else if (typeOrTypes.baseType === "tuple" && typeOrTypes.components) {
            type = typeOrTypes.components[index];
        }
        else if (typeOrTypes.baseType === "array" && typeOrTypes.arrayChildren) {
            type = typeOrTypes.arrayChildren;
        }
        else {
            throw new Error(`Can only use \`matches\` on tuple or array type params, got: \`${typeOrTypes.format("sighash")}\``);
        }
        const expectedType = parameterType(type);
        const scopedType = checkScopedType(condition);
        if (scopedType === expectedType)
            return;
        // allow dynamic type values to be interpreted as calldata or abi encoded
        if (expectedType === ParameterType.Dynamic &&
            (scopedType === ParameterType.Calldata ||
                scopedType === ParameterType.AbiEncoded)) {
            return;
        }
        const fieldReference = type.name ? `'${type.name}'` : `at index ${index}`;
        throw new Error(`Condition for field ${fieldReference} has wrong paramType \`${ParameterType[scopedType]}\` (expected: \`${ParameterType[expectedType]}\`)`);
    });
};
/**
 * Returns `condition.paramType` if it is not `ParameterType.None`, otherwise returns the scoped param type of its children, if any.
 * Throws if the children have mixed scoped param types.
 * @param condition The condition to get the scoped param type of.
 * @returns the `ParameterType` the condition is applied to.
 */
const checkScopedType = (condition) => {
    if (condition.paramType === ParameterType.None) {
        if (!condition.children || condition.children.length === 0) {
            // e.g.: Operator.EtherWithinAllowance / Operator.CallWithinAllowance
            return ParameterType.None;
        }
        const [first, ...rest] = condition.children;
        const result = checkScopedType(first);
        // assert that all following children have compatible types
        rest.forEach((child) => {
            const childType = checkScopedType(child);
            checkParameterTypeCompatibility(result, childType);
        });
        return result;
    }
    return condition.paramType;
};
const arrayDiff = (a, b) => {
    const set = new Set(b);
    return a.filter((x) => !set.has(x));
};
function isBigNumberish(value) {
    return ((typeof value === "number" && value % 1 === 0) ||
        typeof value === "bigint" ||
        (typeof value === "string" && !!value.match(/^-?[0-9]+$/)) ||
        isHexString(value));
}
